// Simultaneous Linear Equations Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const simultaneousEquationsSamples = {
  fundamental: [
    {
      id: 'simul_fund_01',
      problem: "Solve the simultaneous equations: 2x + y = 5, x - y = 1",
      problemType: "simultaneous_equations",
      difficulty: "fundamental",
      description: "Basic elimination method",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "2x + y = 5",
            value: "2x + y = 5",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "x - y = 1",
            value: "x - y = 1",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "elimination_formula",
            name: "Elimination Formula",
            type: "formulas",
            description: "Add equations to eliminate one variable",
            value: "Elimination: Add equations to eliminate y",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_add_equations",
            name: "Step 1: Add Equations",
            type: "steps",
            description: "Add the two equations to eliminate y",
            value: "Step 1: Add equations: 2x + y + x - y = 5 + 1",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_simplify",
            name: "Step 2: Simplify",
            type: "steps",
            description: "Simplify the resulting equation",
            value: "Step 2: 3x = 6",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_solve_for_x",
            name: "Step 3: Solve for x",
            type: "steps",
            description: "Solve the resulting equation for x",
            value: "Step 3: x = 6/3 = 2",
            importance: 10,
            x: 100, y: 0, z: 25
          },
          {
            id: "substitution_formula",
            name: "Substitution Formula",
            type: "formulas",
            description: "Substitute x value into one equation",
            value: "Substitution: Replace x with its value",
            importance: 9,
            x: -150, y: 50, z: 20
          },
          {
            id: "step4_substitute_x",
            name: "Step 4: Substitute x",
            type: "steps",
            description: "Substitute x = 2 into second equation",
            value: "Step 4: 2 - y = 1",
            importance: 9,
            x: -50, y: 100, z: 25
          },
          {
            id: "step5_solve_for_y",
            name: "Step 5: Solve for y",
            type: "steps",
            description: "Solve for y",
            value: "Step 5: -y = 1 - 2 = -1, so y = 1",
            importance: 10,
            x: 50, y: 100, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Substitute x=2, y=1 into both equations",
            importance: 8,
            x: 0, y: 150, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x = 2, y = 1",
            value: "x = 2, y = 1",
            importance: 10,
            x: 0, y: 200, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "equation2", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "elimination_formula", to: "step1_add_equations", type: "applied in", strength: 10 },
          { from: "equation1", to: "step1_add_equations", type: "used in", strength: 9 },
          { from: "equation2", to: "step1_add_equations", type: "used in", strength: 9 },
          { from: "step1_add_equations", to: "step2_simplify", type: "leads to", strength: 10 },
          { from: "step2_simplify", to: "step3_solve_for_x", type: "leads to", strength: 10 },
          { from: "step3_solve_for_x", to: "substitution_formula", type: "enables", strength: 9 },
          { from: "substitution_formula", to: "step4_substitute_x", type: "applied in", strength: 9 },
          { from: "equation2", to: "step4_substitute_x", type: "used in", strength: 9 },
          { from: "step4_substitute_x", to: "step5_solve_for_y", type: "leads to", strength: 10 },
          { from: "step3_solve_for_x", to: "solution_verification", type: "used in", strength: 8 },
          { from: "step5_solve_for_y", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    },
    {
      id: 'simul_fund_02',
      problem: "Solve: x + y = 4, 2x - y = 2",
      problemType: "simultaneous_equations",
      difficulty: "fundamental",
      description: "Simple elimination",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "x + y = 4",
            value: "x + y = 4",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "2x - y = 2",
            value: "2x - y = 2",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "elimination_formula",
            name: "Elimination Formula",
            type: "formulas",
            description: "Add equations to eliminate y",
            value: "Elimination: Add equations to eliminate y",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_add_equations",
            name: "Step 1: Add Equations",
            type: "steps",
            description: "Add the two equations to eliminate y",
            value: "Step 1: Add equations: x + y + 2x - y = 4 + 2",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_simplify",
            name: "Step 2: Simplify",
            type: "steps",
            description: "Simplify the resulting equation",
            value: "Step 2: 3x = 6",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_solve_for_x",
            name: "Step 3: Solve for x",
            type: "steps",
            description: "Solve for x",
            value: "Step 3: x = 6/3 = 2",
            importance: 10,
            x: 100, y: 0, z: 25
          },
          {
            id: "substitution_formula",
            name: "Substitution Formula",
            type: "formulas",
            description: "Substitute x value into first equation",
            value: "Substitution: Replace x with its value",
            importance: 9,
            x: -150, y: 50, z: 20
          },
          {
            id: "step4_substitute_x",
            name: "Step 4: Substitute x",
            type: "steps",
            description: "Substitute x = 2 into first equation",
            value: "Step 4: 2 + y = 4",
            importance: 9,
            x: -50, y: 100, z: 25
          },
          {
            id: "step5_solve_for_y",
            name: "Step 5: Solve for y",
            type: "steps",
            description: "Solve for y",
            value: "Step 5: y = 4 - 2 = 2",
            importance: 10,
            x: 50, y: 100, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Substitute x=2, y=2 into both equations",
            importance: 8,
            x: 0, y: 150, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x = 2, y = 2",
            value: "x = 2, y = 2",
            importance: 10,
            x: 0, y: 200, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "equation2", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "elimination_formula", to: "step1_add_equations", type: "applied in", strength: 10 },
          { from: "equation1", to: "step1_add_equations", type: "used in", strength: 9 },
          { from: "equation2", to: "step1_add_equations", type: "used in", strength: 9 },
          { from: "step1_add_equations", to: "step2_simplify", type: "leads to", strength: 10 },
          { from: "step2_simplify", to: "step3_solve_for_x", type: "leads to", strength: 10 },
          { from: "step3_solve_for_x", to: "substitution_formula", type: "enables", strength: 9 },
          { from: "substitution_formula", to: "step4_substitute_x", type: "applied in", strength: 9 },
          { from: "equation1", to: "step4_substitute_x", type: "used in", strength: 9 },
          { from: "step4_substitute_x", to: "step5_solve_for_y", type: "leads to", strength: 10 },
          { from: "step3_solve_for_x", to: "solution_verification", type: "used in", strength: 8 },
          { from: "step5_solve_for_y", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    },
    {
      id: 'simul_fund_03',
      problem: "Solve: 3x + 2y = 8, x - y = 1",
      problemType: "simultaneous_equations",
      difficulty: "fundamental",
      description: "Substitution method",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "3x + 2y = 8",
            value: "3x + 2y = 8",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "x - y = 1",
            value: "x - y = 1",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "substitution_formula",
            name: "Substitution Formula",
            type: "formulas",
            description: "Express one variable in terms of another",
            value: "Substitution: Express x in terms of y from equation 2",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_express_x",
            name: "Step 1: Express x",
            type: "steps",
            description: "Express x in terms of y",
            value: "Step 1: From x - y = 1, we get x = y + 1",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_substitute",
            name: "Step 2: Substitute",
            type: "steps",
            description: "Substitute x = y + 1 into first equation",
            value: "Step 2: 3(y + 1) + 2y = 8",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_expand",
            name: "Step 3: Expand",
            type: "steps",
            description: "Expand the expression",
            value: "Step 3: 3y + 3 + 2y = 8",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_combine",
            name: "Step 4: Combine Terms",
            type: "steps",
            description: "Combine like terms",
            value: "Step 4: 5y + 3 = 8",
            importance: 9,
            x: 0, y: 50, z: 25
          },
          {
            id: "step5_solve_for_y",
            name: "Step 5: Solve for y",
            type: "steps",
            description: "Solve for y",
            value: "Step 5: 5y = 5, so y = 1",
            importance: 10,
            x: 0, y: 100, z: 25
          },
          {
            id: "step6_find_x",
            name: "Step 6: Find x",
            type: "steps",
            description: "Substitute y = 1 to find x",
            value: "Step 6: x = 1 + 1 = 2",
            importance: 10,
            x: 0, y: 150, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Substitute x=2, y=1 into both equations",
            importance: 8,
            x: 0, y: 200, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x = 2, y = 1",
            value: "x = 2, y = 1",
            importance: 10,
            x: 0, y: 250, z: -15
          }
        ],
        links: [
          { from: "equation2", to: "substitution_formula", type: "used in", strength: 9 },
          { from: "substitution_formula", to: "step1_express_x", type: "applied in", strength: 10 },
          { from: "step1_express_x", to: "step2_substitute", type: "leads to", strength: 10 },
          { from: "equation1", to: "step2_substitute", type: "used in", strength: 9 },
          { from: "step2_substitute", to: "step3_expand", type: "leads to", strength: 10 },
          { from: "step3_expand", to: "step4_combine", type: "leads to", strength: 10 },
          { from: "step4_combine", to: "step5_solve_for_y", type: "leads to", strength: 10 },
          { from: "step5_solve_for_y", to: "step6_find_x", type: "leads to", strength: 10 },
          { from: "step1_express_x", to: "step6_find_x", type: "used in", strength: 9 },
          { from: "step5_solve_for_y", to: "solution_verification", type: "used in", strength: 8 },
          { from: "step6_find_x", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    },
    {
      id: 'simul_fund_04',
      problem: "Solve: x + 2y = 5, 2x + y = 4",
      problemType: "simultaneous_equations",
      difficulty: "fundamental",
      description: "Basic elimination",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "x + 2y = 5",
            value: "x + 2y = 5",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "2x + y = 4",
            value: "2x + y = 4",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "elimination_formula",
            name: "Elimination Formula",
            type: "formulas",
            description: "Multiply first equation by 2 to match x coefficients",
            value: "Elimination: Multiply first equation by 2 to eliminate x",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_multiply_first",
            name: "Step 1: Multiply First Equation",
            type: "steps",
            description: "Multiply first equation by 2",
            value: "Step 1: 2(x + 2y) = 2(5) → 2x + 4y = 10",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_subtract_equations",
            name: "Step 2: Subtract Equations",
            type: "steps",
            description: "Subtract second equation from modified first equation",
            value: "Step 2: (2x + 4y) - (2x + y) = 10 - 4",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_simplify",
            name: "Step 3: Simplify",
            type: "steps",
            description: "Simplify the resulting equation",
            value: "Step 3: 3y = 6",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_solve_for_y",
            name: "Step 4: Solve for y",
            type: "steps",
            description: "Solve for y",
            value: "Step 4: y = 6/3 = 2",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "substitution_formula",
            name: "Substitution Formula",
            type: "formulas",
            description: "Substitute y value into one equation",
            value: "Substitution: Replace y with its value",
            importance: 9,
            x: -150, y: 100, z: 20
          },
          {
            id: "step5_substitute_y",
            name: "Step 5: Substitute y",
            type: "steps",
            description: "Substitute y = 2 into first equation",
            value: "Step 5: x + 2(2) = 5",
            importance: 9,
            x: -50, y: 100, z: 25
          },
          {
            id: "step6_solve_for_x",
            name: "Step 6: Solve for x",
            type: "steps",
            description: "Solve for x",
            value: "Step 6: x + 4 = 5, so x = 1",
            importance: 10,
            x: 50, y: 100, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Substitute x=1, y=2 into both equations",
            importance: 8,
            x: 0, y: 150, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x = 1, y = 2",
            value: "x = 1, y = 2",
            importance: 10,
            x: 0, y: 200, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "elimination_formula", to: "step1_multiply_first", type: "applied in", strength: 10 },
          { from: "step1_multiply_first", to: "step2_subtract_equations", type: "leads to", strength: 10 },
          { from: "equation2", to: "step2_subtract_equations", type: "used in", strength: 9 },
          { from: "step2_subtract_equations", to: "step3_simplify", type: "leads to", strength: 10 },
          { from: "step3_simplify", to: "step4_solve_for_y", type: "leads to", strength: 10 },
          { from: "step4_solve_for_y", to: "substitution_formula", type: "enables", strength: 9 },
          { from: "substitution_formula", to: "step5_substitute_y", type: "applied in", strength: 9 },
          { from: "equation1", to: "step5_substitute_y", type: "used in", strength: 9 },
          { from: "step5_substitute_y", to: "step6_solve_for_x", type: "leads to", strength: 10 },
          { from: "step4_solve_for_y", to: "solution_verification", type: "used in", strength: 8 },
          { from: "step6_solve_for_x", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    },
    {
      id: 'simul_fund_05',
      problem: "Solve: 2x - y = 3, x + y = 6",
      problemType: "simultaneous_equations",
      difficulty: "fundamental",
      description: "Simple addition",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "2x - y = 3",
            value: "2x - y = 3",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "x + y = 6",
            value: "x + y = 6",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "elimination_formula",
            name: "Elimination Formula",
            type: "formulas",
            description: "Add equations to eliminate y",
            value: "Elimination: Add equations to eliminate y",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_add_equations",
            name: "Step 1: Add Equations",
            type: "steps",
            description: "Add the two equations to eliminate y",
            value: "Step 1: (2x - y) + (x + y) = 3 + 6",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_simplify",
            name: "Step 2: Simplify",
            type: "steps",
            description: "Simplify the resulting equation",
            value: "Step 2: 3x = 9",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_solve_for_x",
            name: "Step 3: Solve for x",
            type: "steps",
            description: "Solve for x",
            value: "Step 3: x = 9/3 = 3",
            importance: 10,
            x: 100, y: 0, z: 25
          },
          {
            id: "substitution_formula",
            name: "Substitution Formula",
            type: "formulas",
            description: "Substitute x value into one equation",
            value: "Substitution: Replace x with its value",
            importance: 9,
            x: -150, y: 50, z: 20
          },
          {
            id: "step4_substitute_x",
            name: "Step 4: Substitute x",
            type: "steps",
            description: "Substitute x = 3 into second equation",
            value: "Step 4: 3 + y = 6",
            importance: 9,
            x: -50, y: 100, z: 25
          },
          {
            id: "step5_solve_for_y",
            name: "Step 5: Solve for y",
            type: "steps",
            description: "Solve for y",
            value: "Step 5: y = 6 - 3 = 3",
            importance: 10,
            x: 50, y: 100, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Substitute x=3, y=3 into both equations",
            importance: 8,
            x: 0, y: 150, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x = 3, y = 3",
            value: "x = 3, y = 3",
            importance: 10,
            x: 0, y: 200, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "equation2", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "elimination_formula", to: "step1_add_equations", type: "applied in", strength: 10 },
          { from: "equation1", to: "step1_add_equations", type: "used in", strength: 9 },
          { from: "equation2", to: "step1_add_equations", type: "used in", strength: 9 },
          { from: "step1_add_equations", to: "step2_simplify", type: "leads to", strength: 10 },
          { from: "step2_simplify", to: "step3_solve_for_x", type: "leads to", strength: 10 },
          { from: "step3_solve_for_x", to: "substitution_formula", type: "enables", strength: 9 },
          { from: "substitution_formula", to: "step4_substitute_x", type: "applied in", strength: 9 },
          { from: "equation2", to: "step4_substitute_x", type: "used in", strength: 9 },
          { from: "step4_substitute_x", to: "step5_solve_for_y", type: "leads to", strength: 10 },
          { from: "step3_solve_for_x", to: "solution_verification", type: "used in", strength: 8 },
          { from: "step5_solve_for_y", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'simul_inter_01',
      problem: "Solve: 3x + 2y = 12, 2x + 3y = 13",
      problemType: "simultaneous_equations",
      difficulty: "intermediate",
      description: "Elimination with multiplication",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "3x + 2y = 12",
            value: "3x + 2y = 12",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "2x + 3y = 13",
            value: "2x + 3y = 13",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "elimination_formula",
            name: "Elimination Formula",
            type: "formulas",
            description: "Multiply equations to match coefficients",
            value: "Elimination: Multiply equations to match x coefficients",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_multiply_first",
            name: "Step 1: Multiply First Equation",
            type: "steps",
            description: "Multiply first equation by 2",
            value: "Step 1: 2(3x + 2y) = 2(12) → 6x + 4y = 24",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_multiply_second",
            name: "Step 2: Multiply Second Equation",
            type: "steps",
            description: "Multiply second equation by 3",
            value: "Step 2: 3(2x + 3y) = 3(13) → 6x + 9y = 39",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step3_subtract_equations",
            name: "Step 3: Subtract Equations",
            type: "steps",
            description: "Subtract to eliminate x",
            value: "Step 3: (6x + 9y) - (6x + 4y) = 39 - 24",
            importance: 9,
            x: 0, y: 50, z: 25
          },
          {
            id: "step4_simplify",
            name: "Step 4: Simplify",
            type: "steps",
            description: "Simplify the resulting equation",
            value: "Step 4: 5y = 15",
            importance: 9,
            x: 0, y: 100, z: 25
          },
          {
            id: "step5_solve_for_y",
            name: "Step 5: Solve for y",
            type: "steps",
            description: "Solve for y",
            value: "Step 5: y = 15/5 = 3",
            importance: 10,
            x: 0, y: 150, z: 25
          },
          {
            id: "substitution_formula",
            name: "Substitution Formula",
            type: "formulas",
            description: "Substitute y value into one equation",
            value: "Substitution: Replace y with its value",
            importance: 9,
            x: -150, y: 200, z: 20
          },
          {
            id: "step6_substitute_y",
            name: "Step 6: Substitute y",
            type: "steps",
            description: "Substitute y = 3 into first equation",
            value: "Step 6: 3x + 2(3) = 12",
            importance: 9,
            x: -50, y: 200, z: 25
          },
          {
            id: "step7_solve_for_x",
            name: "Step 7: Solve for x",
            type: "steps",
            description: "Solve for x",
            value: "Step 7: 3x + 6 = 12, so 3x = 6, x = 2",
            importance: 10,
            x: 50, y: 200, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Substitute x=2, y=3 into both equations",
            importance: 8,
            x: 0, y: 250, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x = 2, y = 3",
            value: "x = 2, y = 3",
            importance: 10,
            x: 0, y: 300, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "equation2", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "elimination_formula", to: "step1_multiply_first", type: "applied in", strength: 10 },
          { from: "elimination_formula", to: "step2_multiply_second", type: "applied in", strength: 10 },
          { from: "equation1", to: "step1_multiply_first", type: "used in", strength: 9 },
          { from: "equation2", to: "step2_multiply_second", type: "used in", strength: 9 },
          { from: "step1_multiply_first", to: "step3_subtract_equations", type: "leads to", strength: 10 },
          { from: "step2_multiply_second", to: "step3_subtract_equations", type: "leads to", strength: 10 },
          { from: "step3_subtract_equations", to: "step4_simplify", type: "leads to", strength: 10 },
          { from: "step4_simplify", to: "step5_solve_for_y", type: "leads to", strength: 10 },
          { from: "step5_solve_for_y", to: "substitution_formula", type: "enables", strength: 9 },
          { from: "substitution_formula", to: "step6_substitute_y", type: "applied in", strength: 9 },
          { from: "equation1", to: "step6_substitute_y", type: "used in", strength: 9 },
          { from: "step6_substitute_y", to: "step7_solve_for_x", type: "leads to", strength: 10 },
          { from: "step5_solve_for_y", to: "solution_verification", type: "used in", strength: 8 },
          { from: "step7_solve_for_x", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    },
    {
      id: 'simul_inter_02',
      problem: "Solve: x + 2y + z = 6, 2x + y + z = 7, x + y + 2z = 8",
      problemType: "simultaneous_equations",
      difficulty: "intermediate",
      description: "Three equations with three unknowns",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "x + 2y + z = 6",
            value: "x + 2y + z = 6",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "2x + y + z = 7",
            value: "2x + y + z = 7",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "equation3",
            name: "Third Equation",
            type: "concepts",
            description: "x + y + 2z = 8",
            value: "x + y + 2z = 8",
            importance: 10,
            x: 0, y: -100, z: 0
          },
          {
            id: "elimination_formula",
            name: "Elimination Formula",
            type: "formulas",
            description: "Subtract equations to eliminate z",
            value: "Elimination: Subtract equations to eliminate z",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_subtract_1_2",
            name: "Step 1: Subtract Eq1 from Eq2",
            type: "steps",
            description: "Subtract first equation from second equation",
            value: "Step 1: (2x + y + z) - (x + 2y + z) = 7 - 6",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_simplify_1",
            name: "Step 2: Simplify First Result",
            type: "steps",
            description: "Simplify the first elimination result",
            value: "Step 2: x - y = 1",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_subtract_1_3",
            name: "Step 3: Subtract Eq1 from Eq3",
            type: "steps",
            description: "Subtract first equation from third equation",
            value: "Step 3: (x + y + 2z) - (x + 2y + z) = 8 - 6",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_simplify_2",
            name: "Step 4: Simplify Second Result",
            type: "steps",
            description: "Simplify the second elimination result",
            value: "Step 4: -y + z = 2",
            importance: 9,
            x: 0, y: 50, z: 25
          },
          {
            id: "substitution_formula",
            name: "Substitution Formula",
            type: "formulas",
            description: "Substitute z value to find y",
            value: "Substitution: Replace z with its value",
            importance: 9,
            x: -150, y: 100, z: 20
          },
          {
            id: "step5_solve_for_y",
            name: "Step 5: Solve for y",
            type: "steps",
            description: "Solve for y using the second result",
            value: "Step 5: From -y + z = 2, we get y = z - 2",
            importance: 10,
            x: -50, y: 100, z: 25
          },
          {
            id: "step6_substitute_y",
            name: "Step 6: Substitute y",
            type: "steps",
            description: "Substitute y = z - 2 into first result",
            value: "Step 6: x - (z - 2) = 1",
            importance: 9,
            x: 50, y: 100, z: 25
          },
          {
            id: "step7_solve_for_x",
            name: "Step 7: Solve for x",
            type: "steps",
            description: "Solve for x in terms of z",
            value: "Step 7: x = z - 1",
            importance: 10,
            x: 0, y: 150, z: 25
          },
          {
            id: "step8_substitute_all",
            name: "Step 8: Substitute into Original",
            type: "steps",
            description: "Substitute x and y into first equation",
            value: "Step 8: (z - 1) + 2(z - 2) + z = 6",
            importance: 9,
            x: -50, y: 200, z: 25
          },
          {
            id: "step9_solve_for_z",
            name: "Step 9: Solve for z",
            type: "steps",
            description: "Solve for z",
            value: "Step 9: z - 1 + 2z - 4 + z = 6, so 4z = 11, z = 2.75",
            importance: 10,
            x: 50, y: 200, z: 25
          },
          {
            id: "step10_find_x_y",
            name: "Step 10: Find x and y",
            type: "steps",
            description: "Find x and y using z = 2.75",
            value: "Step 10: x = 2.75 - 1 = 1.75, y = 2.75 - 2 = 0.75",
            importance: 10,
            x: 0, y: 250, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Substitute x=1.75, y=0.75, z=2.75 into all equations",
            importance: 8,
            x: 0, y: 300, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x = 1.75, y = 0.75, z = 2.75",
            value: "x = 1.75, y = 0.75, z = 2.75",
            importance: 10,
            x: 0, y: 350, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "equation2", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "equation3", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "elimination_formula", to: "step1_subtract_1_2", type: "applied in", strength: 10 },
          { from: "elimination_formula", to: "step3_subtract_1_3", type: "applied in", strength: 10 },
          { from: "equation1", to: "step1_subtract_1_2", type: "used in", strength: 9 },
          { from: "equation2", to: "step1_subtract_1_2", type: "used in", strength: 9 },
          { from: "equation1", to: "step3_subtract_1_3", type: "used in", strength: 9 },
          { from: "equation3", to: "step3_subtract_1_3", type: "used in", strength: 9 },
          { from: "step1_subtract_1_2", to: "step2_simplify_1", type: "leads to", strength: 10 },
          { from: "step3_subtract_1_3", to: "step4_simplify_2", type: "leads to", strength: 10 },
          { from: "step4_simplify_2", to: "substitution_formula", type: "enables", strength: 9 },
          { from: "substitution_formula", to: "step5_solve_for_y", type: "applied in", strength: 9 },
          { from: "step2_simplify_1", to: "step6_substitute_y", type: "leads to", strength: 10 },
          { from: "step5_solve_for_y", to: "step6_substitute_y", type: "used in", strength: 9 },
          { from: "step6_substitute_y", to: "step7_solve_for_x", type: "leads to", strength: 10 },
          { from: "step7_solve_for_x", to: "step8_substitute_all", type: "enables", strength: 9 },
          { from: "step5_solve_for_y", to: "step8_substitute_all", type: "used in", strength: 9 },
          { from: "equation1", to: "step8_substitute_all", type: "used in", strength: 9 },
          { from: "step8_substitute_all", to: "step9_solve_for_z", type: "leads to", strength: 10 },
          { from: "step9_solve_for_z", to: "step10_find_x_y", type: "enables", strength: 10 },
          { from: "step7_solve_for_x", to: "step10_find_x_y", type: "used in", strength: 9 },
          { from: "step5_solve_for_y", to: "step10_find_x_y", type: "used in", strength: 9 },
          { from: "step10_find_x_y", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    },
    {
      id: 'simul_inter_03',
      problem: "Solve: 2x + 3y = 8, 4x + 6y = 16",
      problemType: "simultaneous_equations",
      difficulty: "intermediate",
      description: "Dependent equations",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "2x + 3y = 8",
            value: "2x + 3y = 8",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "4x + 6y = 16",
            value: "4x + 6y = 16",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "dependency_check_formula",
            name: "Dependency Check Formula",
            type: "formulas",
            description: "Check if equations are multiples of each other",
            value: "Dependency: Check if one equation is a multiple of another",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_analyze_coefficients",
            name: "Step 1: Analyze Coefficients",
            type: "steps",
            description: "Compare coefficients of both equations",
            value: "Step 1: First equation: 2x + 3y = 8, Second equation: 4x + 6y = 16",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_check_multiplier",
            name: "Step 2: Check Multiplier",
            type: "steps",
            description: "Check if second equation is a multiple of first",
            value: "Step 2: 4x + 6y = 2(2x + 3y) = 2(8) = 16",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_confirm_dependency",
            name: "Step 3: Confirm Dependency",
            type: "steps",
            description: "Confirm equations are dependent",
            value: "Step 3: Second equation = 2 × First equation",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "infinite_solutions_formula",
            name: "Infinite Solutions Formula",
            type: "formulas",
            description: "Dependent equations have infinite solutions",
            value: "Infinite Solutions: When equations are dependent, infinite solutions exist",
            importance: 9,
            x: -150, y: 50, z: 20
          },
          {
            id: "step4_express_solution",
            name: "Step 4: Express Solution",
            type: "steps",
            description: "Express one variable in terms of the other",
            value: "Step 4: From 2x + 3y = 8, solve for x: 2x = 8 - 3y",
            importance: 10,
            x: -50, y: 100, z: 25
          },
          {
            id: "step5_final_solution",
            name: "Step 5: Final Solution",
            type: "steps",
            description: "Express the complete solution",
            value: "Step 5: x = (8 - 3y)/2 = 4 - 1.5y",
            importance: 10,
            x: 50, y: 100, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Substitute x = 4 - 1.5y into both equations",
            importance: 8,
            x: 0, y: 150, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x = 4 - 1.5y (infinite solutions)",
            value: "x = 4 - 1.5y (infinite solutions)",
            importance: 10,
            x: 0, y: 200, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "dependency_check_formula", type: "used in", strength: 9 },
          { from: "equation2", to: "dependency_check_formula", type: "used in", strength: 9 },
          { from: "dependency_check_formula", to: "step1_analyze_coefficients", type: "applied in", strength: 10 },
          { from: "equation1", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "equation2", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "step1_analyze_coefficients", to: "step2_check_multiplier", type: "leads to", strength: 10 },
          { from: "step2_check_multiplier", to: "step3_confirm_dependency", type: "leads to", strength: 10 },
          { from: "step3_confirm_dependency", to: "infinite_solutions_formula", type: "implies", strength: 9 },
          { from: "infinite_solutions_formula", to: "step4_express_solution", type: "enables", strength: 9 },
          { from: "equation1", to: "step4_express_solution", type: "used in", strength: 9 },
          { from: "step4_express_solution", to: "step5_final_solution", type: "leads to", strength: 10 },
          { from: "step5_final_solution", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    },
    {
      id: 'simul_inter_04',
      problem: "Solve: x + y = 5, 2x + 2y = 9",
      problemType: "simultaneous_equations",
      difficulty: "intermediate",
      description: "Inconsistent equations",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "x + y = 5",
            value: "x + y = 5",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "2x + 2y = 9",
            value: "2x + 2y = 9",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "inconsistency_check_formula",
            name: "Inconsistency Check Formula",
            type: "formulas",
            description: "Check if equations are inconsistent",
            value: "Inconsistency: Check if equations lead to a contradiction",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_analyze_coefficients",
            name: "Step 1: Analyze Coefficients",
            type: "steps",
            description: "Compare coefficients of both equations",
            value: "Step 1: First equation: x + y = 5, Second equation: 2x + 2y = 9",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_multiply_first",
            name: "Step 2: Multiply First Equation",
            type: "steps",
            description: "Multiply first equation by 2 to match coefficients",
            value: "Step 2: 2(x + y) = 2(5) → 2x + 2y = 10",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_compare_equations",
            name: "Step 3: Compare Equations",
            type: "steps",
            description: "Compare the modified first equation with second equation",
            value: "Step 3: Modified first: 2x + 2y = 10, Second: 2x + 2y = 9",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_identify_contradiction",
            name: "Step 4: Identify Contradiction",
            type: "steps",
            description: "Identify the contradiction",
            value: "Step 4: 2x + 2y cannot equal both 10 and 9 simultaneously",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "contradiction_formula",
            name: "Contradiction Formula",
            type: "formulas",
            description: "Contradiction indicates no solution",
            value: "Contradiction: When equations lead to 0 = -1, no solution exists",
            importance: 9,
            x: -150, y: 100, z: 20
          },
          {
            id: "step5_subtract_equations",
            name: "Step 5: Subtract Equations",
            type: "steps",
            description: "Subtract second equation from modified first equation",
            value: "Step 5: (2x + 2y) - (2x + 2y) = 10 - 9",
            importance: 9,
            x: -50, y: 100, z: 25
          },
          {
            id: "step6_simplify_contradiction",
            name: "Step 6: Simplify Contradiction",
            type: "steps",
            description: "Simplify to show the contradiction",
            value: "Step 6: 0 = 1, which is a contradiction",
            importance: 10,
            x: 50, y: 100, z: 25
          },
          {
            id: "no_solution_formula",
            name: "No Solution Formula",
            type: "formulas",
            description: "Contradiction implies no solution",
            value: "No Solution: When equations are inconsistent, no solution exists",
            importance: 9,
            x: 0, y: 150, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "No solution exists",
            value: "No solution exists",
            importance: 10,
            x: 0, y: 200, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "inconsistency_check_formula", type: "used in", strength: 9 },
          { from: "equation2", to: "inconsistency_check_formula", type: "used in", strength: 9 },
          { from: "inconsistency_check_formula", to: "step1_analyze_coefficients", type: "applied in", strength: 10 },
          { from: "equation1", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "equation2", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "step1_analyze_coefficients", to: "step2_multiply_first", type: "leads to", strength: 10 },
          { from: "equation1", to: "step2_multiply_first", type: "used in", strength: 9 },
          { from: "step2_multiply_first", to: "step3_compare_equations", type: "leads to", strength: 10 },
          { from: "equation2", to: "step3_compare_equations", type: "used in", strength: 9 },
          { from: "step3_compare_equations", to: "step4_identify_contradiction", type: "leads to", strength: 10 },
          { from: "step4_identify_contradiction", to: "contradiction_formula", type: "implies", strength: 9 },
          { from: "contradiction_formula", to: "step5_subtract_equations", type: "applied in", strength: 9 },
          { from: "step2_multiply_first", to: "step5_subtract_equations", type: "used in", strength: 9 },
          { from: "equation2", to: "step5_subtract_equations", type: "used in", strength: 9 },
          { from: "step5_subtract_equations", to: "step6_simplify_contradiction", type: "leads to", strength: 10 },
          { from: "step6_simplify_contradiction", to: "no_solution_formula", type: "implies", strength: 9 },
          { from: "no_solution_formula", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    },
    {
      id: 'simul_inter_05',
      problem: "Solve: 3x + 4y = 10, 6x + 8y = 20",
      problemType: "simultaneous_equations",
      difficulty: "intermediate",
      description: "Dependent equations with scaling",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "3x + 4y = 10",
            value: "3x + 4y = 10",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "6x + 8y = 20",
            value: "6x + 8y = 20",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "scaling_check_formula",
            name: "Scaling Check Formula",
            type: "formulas",
            description: "Check if equations are scaled versions of each other",
            value: "Scaling: Check if one equation is a scaled version of another",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_analyze_coefficients",
            name: "Step 1: Analyze Coefficients",
            type: "steps",
            description: "Compare coefficients of both equations",
            value: "Step 1: First equation: 3x + 4y = 10, Second equation: 6x + 8y = 20",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_check_scaling_factor",
            name: "Step 2: Check Scaling Factor",
            type: "steps",
            description: "Check if second equation is a multiple of first",
            value: "Step 2: 6x + 8y = 2(3x + 4y) = 2(10) = 20",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_confirm_dependency",
            name: "Step 3: Confirm Dependency",
            type: "steps",
            description: "Confirm equations are dependent",
            value: "Step 3: Second equation = 2 × First equation",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "infinite_solutions_formula",
            name: "Infinite Solutions Formula",
            type: "formulas",
            description: "Dependent equations have infinite solutions",
            value: "Infinite Solutions: When equations are dependent, infinite solutions exist",
            importance: 9,
            x: -150, y: 50, z: 20
          },
          {
            id: "step4_express_solution",
            name: "Step 4: Express Solution",
            type: "steps",
            description: "Express one variable in terms of the other",
            value: "Step 4: From 3x + 4y = 10, solve for x: 3x = 10 - 4y",
            importance: 10,
            x: -50, y: 100, z: 25
          },
          {
            id: "step5_final_solution",
            name: "Step 5: Final Solution",
            type: "steps",
            description: "Express the complete solution",
            value: "Step 5: x = (10 - 4y)/3",
            importance: 10,
            x: 50, y: 100, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Substitute x = (10 - 4y)/3 into both equations",
            importance: 8,
            x: 0, y: 150, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x = (10 - 4y)/3 (infinite solutions)",
            value: "x = (10 - 4y)/3 (infinite solutions)",
            importance: 10,
            x: 0, y: 200, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "scaling_check_formula", type: "used in", strength: 9 },
          { from: "equation2", to: "scaling_check_formula", type: "used in", strength: 9 },
          { from: "scaling_check_formula", to: "step1_analyze_coefficients", type: "applied in", strength: 10 },
          { from: "equation1", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "equation2", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "step1_analyze_coefficients", to: "step2_check_scaling_factor", type: "leads to", strength: 10 },
          { from: "step2_check_scaling_factor", to: "step3_confirm_dependency", type: "leads to", strength: 10 },
          { from: "step3_confirm_dependency", to: "infinite_solutions_formula", type: "implies", strength: 9 },
          { from: "infinite_solutions_formula", to: "step4_express_solution", type: "enables", strength: 9 },
          { from: "equation1", to: "step4_express_solution", type: "used in", strength: 9 },
          { from: "step4_express_solution", to: "step5_final_solution", type: "leads to", strength: 10 },
          { from: "step5_final_solution", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'simul_hard_01',
      problem: "Solve: 2x + 3y + 4z = 20, 3x + 2y + z = 10, x + 4y + 3z = 15",
      problemType: "simultaneous_equations",
      difficulty: "harder",
      description: "Complex three-variable system",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "2x + 3y + 4z = 20",
            value: "2x + 3y + 4z = 20",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "3x + 2y + z = 10",
            value: "3x + 2y + z = 10",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "equation3",
            name: "Third Equation",
            type: "concepts",
            description: "x + 4y + 3z = 15",
            value: "x + 4y + 3z = 15",
            importance: 10,
            x: 0, y: -100, z: 0
          },
          {
            id: "elimination_formula",
            name: "Elimination Formula",
            type: "formulas",
            description: "Eliminate one variable to create two equations in two variables",
            value: "Elimination: Eliminate z to create two equations in x, y",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_multiply_equation2",
            name: "Step 1: Multiply Equation 2",
            type: "steps",
            description: "Multiply second equation by 4 to match z coefficient",
            value: "Step 1: 4(3x + 2y + z) = 4(10) → 12x + 8y + 4z = 40",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_subtract_equation1",
            name: "Step 2: Subtract Equation 1",
            type: "steps",
            description: "Subtract first equation from modified second equation",
            value: "Step 2: (12x + 8y + 4z) - (2x + 3y + 4z) = 40 - 20",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_simplify_first_result",
            name: "Step 3: Simplify First Result",
            type: "steps",
            description: "Simplify the first elimination result",
            value: "Step 3: 10x + 5y = 20",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_multiply_equation3",
            name: "Step 4: Multiply Equation 3",
            type: "steps",
            description: "Multiply third equation by 4 to match z coefficient",
            value: "Step 4: 4(x + 4y + 3z) = 4(15) → 4x + 16y + 12z = 60",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step5_multiply_equation1",
            name: "Step 5: Multiply Equation 1",
            type: "steps",
            description: "Multiply first equation by 3 to match z coefficient",
            value: "Step 5: 3(2x + 3y + 4z) = 3(20) → 6x + 9y + 12z = 60",
            importance: 9,
            x: 0, y: 50, z: 25
          },
          {
            id: "step6_subtract_equations",
            name: "Step 6: Subtract Equations",
            type: "steps",
            description: "Subtract modified first equation from modified third equation",
            value: "Step 6: (4x + 16y + 12z) - (6x + 9y + 12z) = 60 - 60",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "step7_simplify_second_result",
            name: "Step 7: Simplify Second Result",
            type: "steps",
            description: "Simplify the second elimination result",
            value: "Step 7: -2x + 7y = 0",
            importance: 9,
            x: 0, y: 100, z: 25
          },
          {
            id: "substitution_formula",
            name: "Substitution Formula",
            type: "formulas",
            description: "Solve the two-variable system",
            value: "Substitution: Solve the system of two equations in x, y",
            importance: 9,
            x: -150, y: 150, z: 20
          },
          {
            id: "step8_solve_for_x",
            name: "Step 8: Solve for x",
            type: "steps",
            description: "Solve for x from the second result",
            value: "Step 8: From -2x + 7y = 0, we get x = 3.5y",
            importance: 10,
            x: -50, y: 150, z: 25
          },
          {
            id: "step9_substitute_x",
            name: "Step 9: Substitute x",
            type: "steps",
            description: "Substitute x = 3.5y into first result",
            value: "Step 9: 10(3.5y) + 5y = 20",
            importance: 9,
            x: 50, y: 150, z: 25
          },
          {
            id: "step10_solve_for_y",
            name: "Step 10: Solve for y",
            type: "steps",
            description: "Solve for y",
            value: "Step 10: 35y + 5y = 20, so 40y = 20, y = 0.5",
            importance: 10,
            x: 0, y: 200, z: 25
          },
          {
            id: "step11_find_x",
            name: "Step 11: Find x",
            type: "steps",
            description: "Find x using y = 0.5",
            value: "Step 11: x = 3.5(0.5) = 1.75",
            importance: 10,
            x: -50, y: 250, z: 25
          },
          {
            id: "step12_substitute_x_y",
            name: "Step 12: Substitute x, y",
            type: "steps",
            description: "Substitute x and y into one of the original equations",
            value: "Step 12: 2(1.75) + 3(0.5) + 4z = 20",
            importance: 9,
            x: 50, y: 250, z: 25
          },
          {
            id: "step13_solve_for_z",
            name: "Step 13: Solve for z",
            type: "steps",
            description: "Solve for z",
            value: "Step 13: 3.5 + 1.5 + 4z = 20, so 4z = 15, z = 3.75",
            importance: 10,
            x: 0, y: 300, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Substitute x=1.75, y=0.5, z=3.75 into all equations",
            importance: 8,
            x: 0, y: 350, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x = 1.75, y = 0.5, z = 3.75",
            value: "x = 1.75, y = 0.5, z = 3.75",
            importance: 10,
            x: 0, y: 400, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "equation2", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "equation3", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "elimination_formula", to: "step1_multiply_equation2", type: "applied in", strength: 10 },
          { from: "elimination_formula", to: "step4_multiply_equation3", type: "applied in", strength: 10 },
          { from: "equation2", to: "step1_multiply_equation2", type: "used in", strength: 9 },
          { from: "equation3", to: "step4_multiply_equation3", type: "used in", strength: 9 },
          { from: "equation1", to: "step2_subtract_equation1", type: "used in", strength: 9 },
          { from: "step1_multiply_equation2", to: "step2_subtract_equation1", type: "leads to", strength: 10 },
          { from: "step2_subtract_equation1", to: "step3_simplify_first_result", type: "leads to", strength: 10 },
          { from: "step4_multiply_equation3", to: "step6_subtract_equations", type: "leads to", strength: 10 },
          { from: "equation1", to: "step5_multiply_equation1", type: "used in", strength: 9 },
          { from: "step5_multiply_equation1", to: "step6_subtract_equations", type: "used in", strength: 9 },
          { from: "step6_subtract_equations", to: "step7_simplify_second_result", type: "leads to", strength: 10 },
          { from: "step3_simplify_first_result", to: "substitution_formula", type: "enables", strength: 9 },
          { from: "step7_simplify_second_result", to: "substitution_formula", type: "enables", strength: 9 },
          { from: "substitution_formula", to: "step8_solve_for_x", type: "applied in", strength: 9 },
          { from: "step7_simplify_second_result", to: "step8_solve_for_x", type: "used in", strength: 9 },
          { from: "step8_solve_for_x", to: "step9_substitute_x", type: "enables", strength: 9 },
          { from: "step3_simplify_first_result", to: "step9_substitute_x", type: "used in", strength: 9 },
          { from: "step9_substitute_x", to: "step10_solve_for_y", type: "leads to", strength: 10 },
          { from: "step10_solve_for_y", to: "step11_find_x", type: "enables", strength: 10 },
          { from: "step8_solve_for_x", to: "step11_find_x", type: "used in", strength: 9 },
          { from: "step11_find_x", to: "step12_substitute_x_y", type: "enables", strength: 9 },
          { from: "step10_solve_for_y", to: "step12_substitute_x_y", type: "used in", strength: 9 },
          { from: "equation1", to: "step12_substitute_x_y", type: "used in", strength: 9 },
          { from: "step12_substitute_x_y", to: "step13_solve_for_z", type: "leads to", strength: 10 },
          { from: "step11_find_x", to: "solution_verification", type: "used in", strength: 8 },
          { from: "step10_solve_for_y", to: "solution_verification", type: "used in", strength: 8 },
          { from: "step13_solve_for_z", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    },
    {
      id: 'simul_hard_02',
      problem: "Solve: x + 2y + 3z = 6, 2x + 4y + 6z = 12, 3x + 6y + 9z = 18",
      problemType: "simultaneous_equations",
      difficulty: "harder",
      description: "Dependent three-variable system",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "x + 2y + 3z = 6",
            value: "x + 2y + 3z = 6",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "2x + 4y + 6z = 12",
            value: "2x + 4y + 6z = 12",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "equation3",
            name: "Third Equation",
            type: "concepts",
            description: "3x + 6y + 9z = 18",
            value: "3x + 6y + 9z = 18",
            importance: 10,
            x: 0, y: -100, z: 0
          },
          {
            id: "dependency_check_formula",
            name: "Dependency Check Formula",
            type: "formulas",
            description: "Check if all equations are multiples of each other",
            value: "Dependency: Check if all equations are multiples of the first equation",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_analyze_coefficients",
            name: "Step 1: Analyze Coefficients",
            type: "steps",
            description: "Compare coefficients of all three equations",
            value: "Step 1: First: x + 2y + 3z = 6, Second: 2x + 4y + 6z = 12, Third: 3x + 6y + 9z = 18",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_check_second_equation",
            name: "Step 2: Check Second Equation",
            type: "steps",
            description: "Check if second equation is a multiple of first",
            value: "Step 2: 2x + 4y + 6z = 2(x + 2y + 3z) = 2(6) = 12",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_check_third_equation",
            name: "Step 3: Check Third Equation",
            type: "steps",
            description: "Check if third equation is a multiple of first",
            value: "Step 3: 3x + 6y + 9z = 3(x + 2y + 3z) = 3(6) = 18",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_confirm_dependency",
            name: "Step 4: Confirm Dependency",
            type: "steps",
            description: "Confirm all equations are dependent",
            value: "Step 4: Second = 2×First, Third = 3×First",
            importance: 9,
            x: 0, y: 50, z: 25
          },
          {
            id: "infinite_solutions_formula",
            name: "Infinite Solutions Formula",
            type: "formulas",
            description: "Dependent equations have infinite solutions",
            value: "Infinite Solutions: When all equations are dependent, infinite solutions exist",
            importance: 9,
            x: -150, y: 100, z: 20
          },
          {
            id: "step5_express_solution",
            name: "Step 5: Express Solution",
            type: "steps",
            description: "Express one variable in terms of the others",
            value: "Step 5: From x + 2y + 3z = 6, solve for x: x = 6 - 2y - 3z",
            importance: 10,
            x: -50, y: 100, z: 25
          },
          {
            id: "step6_parameter_solution",
            name: "Step 6: Parameter Solution",
            type: "steps",
            description: "Express the complete solution with parameters",
            value: "Step 6: x = 6 - 2y - 3z (where y and z are parameters)",
            importance: 10,
            x: 50, y: 100, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Substitute x = 6 - 2y - 3z into all equations",
            importance: 8,
            x: 0, y: 150, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x = 6 - 2y - 3z (infinite solutions)",
            value: "x = 6 - 2y - 3z (infinite solutions)",
            importance: 10,
            x: 0, y: 200, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "dependency_check_formula", type: "used in", strength: 9 },
          { from: "equation2", to: "dependency_check_formula", type: "used in", strength: 9 },
          { from: "equation3", to: "dependency_check_formula", type: "used in", strength: 9 },
          { from: "dependency_check_formula", to: "step1_analyze_coefficients", type: "applied in", strength: 10 },
          { from: "equation1", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "equation2", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "equation3", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "step1_analyze_coefficients", to: "step2_check_second_equation", type: "leads to", strength: 10 },
          { from: "step1_analyze_coefficients", to: "step3_check_third_equation", type: "leads to", strength: 10 },
          { from: "step2_check_second_equation", to: "step4_confirm_dependency", type: "leads to", strength: 10 },
          { from: "step3_check_third_equation", to: "step4_confirm_dependency", type: "leads to", strength: 10 },
          { from: "step4_confirm_dependency", to: "infinite_solutions_formula", type: "implies", strength: 9 },
          { from: "infinite_solutions_formula", to: "step5_express_solution", type: "enables", strength: 9 },
          { from: "equation1", to: "step5_express_solution", type: "used in", strength: 9 },
          { from: "step5_express_solution", to: "step6_parameter_solution", type: "leads to", strength: 10 },
          { from: "step6_parameter_solution", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    },
    {
      id: 'simul_hard_03',
      problem: "Solve: x + y + z = 6, 2x + 3y + 4z = 20, 3x + 4y + 5z = 26",
      problemType: "simultaneous_equations",
      difficulty: "harder",
      description: "Three-variable system with unique solution",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "x + y + z = 6",
            value: "x + y + z = 6",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "2x + 3y + 4z = 20",
            value: "2x + 3y + 4z = 20",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "equation3",
            name: "Third Equation",
            type: "concepts",
            description: "3x + 4y + 5z = 26",
            value: "3x + 4y + 5z = 26",
            importance: 10,
            x: 0, y: -100, z: 0
          },
          {
            id: "elimination_formula",
            name: "Elimination Formula",
            type: "formulas",
            description: "Eliminate one variable to create two equations in two variables",
            value: "Elimination: Eliminate x to create two equations in y, z",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_multiply_equation1",
            name: "Step 1: Multiply Equation 1",
            type: "steps",
            description: "Multiply first equation by 2 to match x coefficient",
            value: "Step 1: 2(x + y + z) = 2(6) → 2x + 2y + 2z = 12",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_subtract_from_equation2",
            name: "Step 2: Subtract from Equation 2",
            type: "steps",
            description: "Subtract modified first equation from second equation",
            value: "Step 2: (2x + 3y + 4z) - (2x + 2y + 2z) = 20 - 12",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_simplify_first_result",
            name: "Step 3: Simplify First Result",
            type: "steps",
            description: "Simplify the first elimination result",
            value: "Step 3: y + 2z = 8",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_multiply_equation1_again",
            name: "Step 4: Multiply Equation 1 Again",
            type: "steps",
            description: "Multiply first equation by 3 to match x coefficient",
            value: "Step 4: 3(x + y + z) = 3(6) → 3x + 3y + 3z = 18",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step5_subtract_from_equation3",
            name: "Step 5: Subtract from Equation 3",
            type: "steps",
            description: "Subtract modified first equation from third equation",
            value: "Step 5: (3x + 4y + 5z) - (3x + 3y + 3z) = 26 - 18",
            importance: 9,
            x: 0, y: 50, z: 25
          },
          {
            id: "step6_simplify_second_result",
            name: "Step 6: Simplify Second Result",
            type: "steps",
            description: "Simplify the second elimination result",
            value: "Step 6: y + 2z = 8",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "substitution_formula",
            name: "Substitution Formula",
            type: "formulas",
            description: "Solve the two-variable system",
            value: "Substitution: Solve the system of two equations in y, z",
            importance: 9,
            x: -150, y: 100, z: 20
          },
          {
            id: "step7_compare_equations",
            name: "Step 7: Compare Equations",
            type: "steps",
            description: "Compare the two elimination results",
            value: "Step 7: Both results give y + 2z = 8",
            importance: 9,
            x: 0, y: 100, z: 25
          },
          {
            id: "step8_express_y",
            name: "Step 8: Express y",
            type: "steps",
            description: "Express y in terms of z",
            value: "Step 8: From y + 2z = 8, we get y = 8 - 2z",
            importance: 10,
            x: -50, y: 150, z: 25
          },
          {
            id: "step9_substitute_y",
            name: "Step 9: Substitute y",
            type: "steps",
            description: "Substitute y = 8 - 2z into first equation",
            value: "Step 9: x + (8 - 2z) + z = 6",
            importance: 9,
            x: 50, y: 150, z: 25
          },
          {
            id: "step10_simplify_for_x",
            name: "Step 10: Simplify for x",
            type: "steps",
            description: "Simplify to find x in terms of z",
            value: "Step 10: x + 8 - z = 6, so x = z - 2",
            importance: 10,
            x: 0, y: 200, z: 25
          },
          {
            id: "step11_substitute_both",
            name: "Step 11: Substitute Both",
            type: "steps",
            description: "Substitute x and y into second equation",
            value: "Step 11: 2(z - 2) + 3(8 - 2z) + 4z = 20",
            importance: 9,
            x: -50, y: 250, z: 25
          },
          {
            id: "step12_solve_for_z",
            name: "Step 12: Solve for z",
            type: "steps",
            description: "Solve for z",
            value: "Step 12: 2z - 4 + 24 - 6z + 4z = 20, so 20 = 20",
            importance: 10,
            x: 50, y: 250, z: 25
          },
          {
            id: "step13_find_y_x",
            name: "Step 13: Find y and x",
            type: "steps",
            description: "Find y and x using z = 3",
            value: "Step 13: y = 8 - 2(3) = 2, x = 3 - 2 = 1",
            importance: 10,
            x: 0, y: 300, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Substitute x=1, y=2, z=3 into all equations",
            importance: 8,
            x: 0, y: 350, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x = 1, y = 2, z = 3",
            value: "x = 1, y = 2, z = 3",
            importance: 10,
            x: 0, y: 400, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "equation2", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "equation3", to: "elimination_formula", type: "used in", strength: 9 },
          { from: "elimination_formula", to: "step1_multiply_equation1", type: "applied in", strength: 10 },
          { from: "elimination_formula", to: "step4_multiply_equation1_again", type: "applied in", strength: 10 },
          { from: "equation1", to: "step1_multiply_equation1", type: "used in", strength: 9 },
          { from: "equation1", to: "step4_multiply_equation1_again", type: "used in", strength: 9 },
          { from: "equation2", to: "step2_subtract_from_equation2", type: "used in", strength: 9 },
          { from: "equation3", to: "step5_subtract_from_equation3", type: "used in", strength: 9 },
          { from: "step1_multiply_equation1", to: "step2_subtract_from_equation2", type: "leads to", strength: 10 },
          { from: "step2_subtract_from_equation2", to: "step3_simplify_first_result", type: "leads to", strength: 10 },
          { from: "step4_multiply_equation1_again", to: "step5_subtract_from_equation3", type: "leads to", strength: 10 },
          { from: "step5_subtract_from_equation3", to: "step6_simplify_second_result", type: "leads to", strength: 10 },
          { from: "step3_simplify_first_result", to: "substitution_formula", type: "enables", strength: 9 },
          { from: "step6_simplify_second_result", to: "substitution_formula", type: "enables", strength: 9 },
          { from: "substitution_formula", to: "step7_compare_equations", type: "applied in", strength: 9 },
          { from: "step7_compare_equations", to: "step8_express_y", type: "leads to", strength: 10 },
          { from: "step8_express_y", to: "step9_substitute_y", type: "enables", strength: 9 },
          { from: "equation1", to: "step9_substitute_y", type: "used in", strength: 9 },
          { from: "step9_substitute_y", to: "step10_simplify_for_x", type: "leads to", strength: 10 },
          { from: "step10_simplify_for_x", to: "step11_substitute_both", type: "enables", strength: 9 },
          { from: "step8_express_y", to: "step11_substitute_both", type: "used in", strength: 9 },
          { from: "equation2", to: "step11_substitute_both", type: "used in", strength: 9 },
          { from: "step11_substitute_both", to: "step12_solve_for_z", type: "leads to", strength: 10 },
          { from: "step12_solve_for_z", to: "step13_find_y_x", type: "enables", strength: 10 },
          { from: "step8_express_y", to: "step13_find_y_x", type: "used in", strength: 9 },
          { from: "step10_simplify_for_x", to: "step13_find_y_x", type: "used in", strength: 9 },
          { from: "step13_find_y_x", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    },
    {
      id: 'simul_hard_04',
      problem: "Solve: x + 2y + 3z = 4, 2x + 4y + 6z = 8, 3x + 6y + 9z = 12",
      problemType: "simultaneous_equations",
      difficulty: "harder",
      description: "Dependent system with parameter",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "x + 2y + 3z = 4",
            value: "x + 2y + 3z = 4",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "2x + 4y + 6z = 8",
            value: "2x + 4y + 6z = 8",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "equation3",
            name: "Third Equation",
            type: "concepts",
            description: "3x + 6y + 9z = 12",
            value: "3x + 6y + 9z = 12",
            importance: 10,
            x: 0, y: -100, z: 0
          },
          {
            id: "dependency_check_formula",
            name: "Dependency Check Formula",
            type: "formulas",
            description: "Check if all equations are multiples of each other",
            value: "Dependency: Check if all equations are multiples of the first equation",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_analyze_coefficients",
            name: "Step 1: Analyze Coefficients",
            type: "steps",
            description: "Compare coefficients of all three equations",
            value: "Step 1: First: x + 2y + 3z = 4, Second: 2x + 4y + 6z = 8, Third: 3x + 6y + 9z = 12",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_check_second_equation",
            name: "Step 2: Check Second Equation",
            type: "steps",
            description: "Check if second equation is a multiple of first",
            value: "Step 2: 2x + 4y + 6z = 2(x + 2y + 3z) = 2(4) = 8",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_check_third_equation",
            name: "Step 3: Check Third Equation",
            type: "steps",
            description: "Check if third equation is a multiple of first",
            value: "Step 3: 3x + 6y + 9z = 3(x + 2y + 3z) = 3(4) = 12",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_confirm_dependency",
            name: "Step 4: Confirm Dependency",
            type: "steps",
            description: "Confirm all equations are dependent",
            value: "Step 4: Second = 2×First, Third = 3×First",
            importance: 9,
            x: 0, y: 50, z: 25
          },
          {
            id: "parameter_solution_formula",
            name: "Parameter Solution Formula",
            type: "formulas",
            description: "Express solution in terms of parameters",
            value: "Parameter Solution: Express one variable in terms of the others",
            importance: 9,
            x: -150, y: 100, z: 20
          },
          {
            id: "step5_express_x",
            name: "Step 5: Express x",
            type: "steps",
            description: "Express x in terms of y and z",
            value: "Step 5: From x + 2y + 3z = 4, solve for x: x = 4 - 2y - 3z",
            importance: 10,
            x: -50, y: 100, z: 25
          },
          {
            id: "step6_parameter_form",
            name: "Step 6: Parameter Form",
            type: "steps",
            description: "Express the complete solution with parameters",
            value: "Step 6: x = 4 - 2y - 3z (where y and z are parameters)",
            importance: 10,
            x: 50, y: 100, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Substitute x = 4 - 2y - 3z into all equations",
            importance: 8,
            x: 0, y: 150, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x = 4 - 2y - 3z (infinite solutions)",
            value: "x = 4 - 2y - 3z (infinite solutions)",
            importance: 10,
            x: 0, y: 200, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "dependency_check_formula", type: "used in", strength: 9 },
          { from: "equation2", to: "dependency_check_formula", type: "used in", strength: 9 },
          { from: "equation3", to: "dependency_check_formula", type: "used in", strength: 9 },
          { from: "dependency_check_formula", to: "step1_analyze_coefficients", type: "applied in", strength: 10 },
          { from: "equation1", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "equation2", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "equation3", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "step1_analyze_coefficients", to: "step2_check_second_equation", type: "leads to", strength: 10 },
          { from: "step1_analyze_coefficients", to: "step3_check_third_equation", type: "leads to", strength: 10 },
          { from: "step2_check_second_equation", to: "step4_confirm_dependency", type: "leads to", strength: 10 },
          { from: "step3_check_third_equation", to: "step4_confirm_dependency", type: "leads to", strength: 10 },
          { from: "step4_confirm_dependency", to: "parameter_solution_formula", type: "implies", strength: 9 },
          { from: "parameter_solution_formula", to: "step5_express_x", type: "applied in", strength: 9 },
          { from: "equation1", to: "step5_express_x", type: "used in", strength: 9 },
          { from: "step5_express_x", to: "step6_parameter_form", type: "leads to", strength: 10 },
          { from: "step6_parameter_form", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    },
    {
      id: 'simul_hard_05',
      problem: "Solve: x + y + z = 3, 2x + 2y + 2z = 6, 3x + 3y + 3z = 9",
      problemType: "simultaneous_equations",
      difficulty: "harder",
      description: "Identical equations",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "First Equation",
            type: "concepts",
            description: "x + y + z = 3",
            value: "x + y + z = 3",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Second Equation",
            type: "concepts",
            description: "2x + 2y + 2z = 6",
            value: "2x + 2y + 2z = 6",
            importance: 10,
            x: 0, y: -150, z: 0
          },
          {
            id: "equation3",
            name: "Third Equation",
            type: "concepts",
            description: "3x + 3y + 3z = 9",
            value: "3x + 3y + 3z = 9",
            importance: 10,
            x: 0, y: -100, z: 0
          },
          {
            id: "identity_check_formula",
            name: "Identity Check Formula",
            type: "formulas",
            description: "Check if all equations are identical",
            value: "Identity: Check if all equations are identical or multiples of each other",
            importance: 10,
            x: -150, y: -50, z: 20
          },
          {
            id: "step1_analyze_coefficients",
            name: "Step 1: Analyze Coefficients",
            type: "steps",
            description: "Compare coefficients of all three equations",
            value: "Step 1: First: x + y + z = 3, Second: 2x + 2y + 2z = 6, Third: 3x + 3y + 3z = 9",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_check_second_equation",
            name: "Step 2: Check Second Equation",
            type: "steps",
            description: "Check if second equation is a multiple of first",
            value: "Step 2: 2x + 2y + 2z = 2(x + y + z) = 2(3) = 6",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_check_third_equation",
            name: "Step 3: Check Third Equation",
            type: "steps",
            description: "Check if third equation is a multiple of first",
            value: "Step 3: 3x + 3y + 3z = 3(x + y + z) = 3(3) = 9",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_confirm_identity",
            name: "Step 4: Confirm Identity",
            type: "steps",
            description: "Confirm all equations are identical",
            value: "Step 4: Second = 2×First, Third = 3×First",
            importance: 9,
            x: 0, y: 50, z: 25
          },
          {
            id: "infinite_solutions_formula",
            name: "Infinite Solutions Formula",
            type: "formulas",
            description: "Identical equations have infinite solutions",
            value: "Infinite Solutions: When all equations are identical, infinite solutions exist",
            importance: 9,
            x: -150, y: 100, z: 20
          },
          {
            id: "step5_express_constraint",
            name: "Step 5: Express Constraint",
            type: "steps",
            description: "Express the single constraint equation",
            value: "Step 5: The only constraint is x + y + z = 3",
            importance: 10,
            x: -50, y: 100, z: 25
          },
          {
            id: "step6_parameter_solution",
            name: "Step 6: Parameter Solution",
            type: "steps",
            description: "Express solution with parameters",
            value: "Step 6: x + y + z = 3 (where any two variables can be parameters)",
            importance: 10,
            x: 50, y: 100, z: 25
          },
          {
            id: "solution_verification",
            name: "Solution Verification",
            type: "formulas",
            description: "Verify solution by substitution",
            value: "Verification: Any values satisfying x + y + z = 3 work for all equations",
            importance: 8,
            x: 0, y: 150, z: 20
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x + y + z = 3 (infinite solutions)",
            value: "x + y + z = 3 (infinite solutions)",
            importance: 10,
            x: 0, y: 200, z: -15
          }
        ],
        links: [
          { from: "equation1", to: "identity_check_formula", type: "used in", strength: 9 },
          { from: "equation2", to: "identity_check_formula", type: "used in", strength: 9 },
          { from: "equation3", to: "identity_check_formula", type: "used in", strength: 9 },
          { from: "identity_check_formula", to: "step1_analyze_coefficients", type: "applied in", strength: 10 },
          { from: "equation1", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "equation2", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "equation3", to: "step1_analyze_coefficients", type: "used in", strength: 9 },
          { from: "step1_analyze_coefficients", to: "step2_check_second_equation", type: "leads to", strength: 10 },
          { from: "step1_analyze_coefficients", to: "step3_check_third_equation", type: "leads to", strength: 10 },
          { from: "step2_check_second_equation", to: "step4_confirm_identity", type: "leads to", strength: 10 },
          { from: "step3_check_third_equation", to: "step4_confirm_identity", type: "leads to", strength: 10 },
          { from: "step4_confirm_identity", to: "infinite_solutions_formula", type: "implies", strength: 9 },
          { from: "infinite_solutions_formula", to: "step5_express_constraint", type: "enables", strength: 9 },
          { from: "equation1", to: "step5_express_constraint", type: "used in", strength: 9 },
          { from: "step5_express_constraint", to: "step6_parameter_solution", type: "leads to", strength: 10 },
          { from: "step6_parameter_solution", to: "solution_verification", type: "used in", strength: 8 },
          { from: "solution_verification", to: "final_answer", type: "confirms", strength: 10 }
        ]
      }
    }
  ]
}; 